import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { fetchJson } from '../lib/fetchJson';
import type { BBox, Region } from '../lib/geo';

const URL_BASE = 'https://maps.water.noaa.gov/server/rest/services/nwm/ana_inundation_extent/FeatureServer/0/query';

export const FIM = {
  maxLatSpan: 0.1,
  maxLonSpan: 0.15,
  bboxQuantizationCells: 5,
  geometryPrecision: 5,
} as const;

export interface FIMRequest {
  url: string;
  bbox: BBox;
  maxAllowableOffset: number;
}

/**
 * Builds the FeatureServer query for a viewport exactly as FloodDataManager
 * does: cap the span, snap the box to a 5-cell grid so neighboring pans reuse
 * the HTTP cache, and ask the server to simplify to about one pixel.
 */
export function buildFIMRequest(region: Region): FIMRequest {
  const latSpan = Math.min(region.latDelta, FIM.maxLatSpan);
  const lonSpan = Math.min(region.lonDelta, FIM.maxLonSpan);
  const rawLatMin = region.center.lat - latSpan / 2;
  const rawLatMax = region.center.lat + latSpan / 2;
  const rawLonMin = region.center.lng - lonSpan / 2;
  const rawLonMax = region.center.lng + lonSpan / 2;

  const cellLat = latSpan / FIM.bboxQuantizationCells;
  const cellLon = lonSpan / FIM.bboxQuantizationCells;
  const bbox: BBox = {
    minLat: Math.floor(rawLatMin / cellLat) * cellLat,
    maxLat: Math.ceil(rawLatMax / cellLat) * cellLat,
    minLon: Math.floor(rawLonMin / cellLon) * cellLon,
    maxLon: Math.ceil(rawLonMax / cellLon) * cellLon,
  };
  const maxAllowableOffset = Math.max(latSpan, lonSpan) / 250;

  const geometry = [bbox.minLon, bbox.minLat, bbox.maxLon, bbox.maxLat].map((v) => v.toFixed(5)).join(',');
  const url =
    `${URL_BASE}?geometry=${geometry}` +
    '&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects' +
    `&outSR=4326&maxAllowableOffset=${maxAllowableOffset.toFixed(6)}&geometryPrecision=${FIM.geometryPrecision}&f=geojson`;
  return { url, bbox, maxAllowableOffset };
}

export function bboxCovers(outer: BBox, inner: BBox): boolean {
  return outer.minLat <= inner.minLat && outer.maxLat >= inner.maxLat && outer.minLon <= inner.minLon && outer.maxLon >= inner.maxLon;
}

export type FIMCollection = FeatureCollection<Polygon | MultiPolygon>;

interface ESRIErrorEnvelope {
  error?: { code: number; message: string };
}

export class ESRIError extends Error {
  constructor(readonly code: number, message: string) {
    super(message);
    this.name = 'ESRIError';
  }
}

export async function fetchFIM(url: string, signal?: AbortSignal): Promise<FIMCollection> {
  const data = await fetchJson<FIMCollection & ESRIErrorEnvelope>(url, { signal, timeoutMs: 30_000 });
  if (data.error) throw new ESRIError(data.error.code, data.error.message);
  if (data.type !== 'FeatureCollection') return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: (data.features ?? []).filter((f) => f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')),
  };
}
